/**
 * Select — the ONE dropdown of the web app. Never a native select element
 * (operator rule, 2026-09-16): the OS popup paints in its own theme and can
 * show neither a flag, a face nor a price line on a dark branded surface.
 *
 * A trigger button + an absolutely positioned listbox — no portal, no
 * dependency (hosts close their own popovers with a DOM `contains` check, so
 * the list has to stay inside the root). Focus moves INTO the listbox while
 * it is open (the collapsible-listbox pattern), so `aria-activedescendant`
 * sits on the element that has focus; Escape / Tab / a pick hand focus back
 * to the trigger. The key rules live in `lib/selectNav.ts`, unit-tested
 * without a DOM.
 *
 * Placement: opens downward, flips upward when there is no room below — room
 * measured against the nearest ancestors that clip (`overflow` other than
 * visible), not just the viewport, since a scrolling column would otherwise
 * cut the list at its edge. Opening never scrolls the page: only the list's
 * own scrollTop moves, to reveal the selected row.
 */
import { Check, ChevronDown } from "lucide-react";
import {
  useEffect,
  useId,
  useLayoutEffect,
  useRef,
  useState,
  type CSSProperties,
  type FocusEvent,
  type KeyboardEvent,
  type ReactNode,
} from "react";
import { firstEnabled, lastEnabled, nextEnabledIndex, typeAheadIndex } from "../../lib/selectNav.ts";
import { fieldLabelStyle } from "../styles.ts";

export interface SelectOption {
  value: string;
  label: string;
  /** Muted, right-aligned (a price, a provenance). Ellipsised when it fights the label. */
  hint?: string;
  /** Sits left of the label in a 22 px tile (a flag, a glyph). */
  icon?: ReactNode;
  disabled?: boolean;
  /** Merged into the label span — how a font list shows each row in its own face. */
  style?: CSSProperties;
}

export type SelectSize = "sm" | "md" | "lg";

export interface SelectProps {
  value: string;
  options: SelectOption[];
  onChange: (value: string) => void;
  /** Required unless `label` is given. */
  "aria-label"?: string;
  /** Rendered above the trigger in the app's field-label style. */
  label?: string;
  size?: SelectSize;
  placeholder?: string;
  disabled?: boolean;
  /** The root wrapper — where a caller sets width / maxWidth / flex. */
  style?: CSSProperties;
  title?: string;
  /** Marks the trigger as the step's first control (`StepFrame` focuses `[data-autofocus]`). */
  "data-autofocus"?: boolean;
  /**
   * Wizard protocol: Return on the CLOSED trigger means "this is my answer"
   * and must advance the step, as it did on the native select. The trigger
   * then carries `data-enter-submits`, swallows the button's implicit click
   * (so the list does not open) and lets the keydown bubble to `StepFrame`,
   * which submits. While OPEN, focus is in the listbox and Enter picks a row
   * there — stopped, so it never reaches the frame.
   */
  enterSubmits?: boolean;
}

const SIZE: Record<SelectSize, { padding: string; fontSize: number }> = {
  sm: { padding: "5px 8px", fontSize: 11.5 },
  md: { padding: "8px 10px", fontSize: 13 },
  lg: { padding: "12px 14px", fontSize: 15 },
};

/** Keys typed faster than this are one type-ahead word. */
const TYPEAHEAD_RESET_MS = 600;
/** Gap between the trigger and the list. */
const GAP = 4;

const ELLIPSIS: CSSProperties = { minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" };

const TILE: CSSProperties = {
  width: 22, height: 22, borderRadius: 6, flexShrink: 0, background: "rgba(255,255,255,.05)",
  display: "inline-flex", alignItems: "center", justifyContent: "center",
};
const IconTile = ({ children }: { children: ReactNode }): React.JSX.Element => (
  <span aria-hidden style={TILE}>{children}</span>
);

/** The vertical band an element can be seen in: the viewport, cut down by
 *  every ancestor that clips its overflow (a scrolling column, a card with
 *  `overflow: hidden`). */
function visibleBand(el: HTMLElement): { top: number; bottom: number } {
  let top = 0;
  let bottom = window.innerHeight;
  for (let p = el.parentElement; p && p !== document.body; p = p.parentElement) {
    const o = getComputedStyle(p);
    if (o.overflowY === "visible" && o.overflow === "visible") continue;
    const r = p.getBoundingClientRect();
    top = Math.max(top, r.top);
    bottom = Math.min(bottom, r.bottom);
  }
  return { top, bottom };
}

/** Scroll the LIST so `row` is inside it — never `scrollIntoView`, which
 *  would also scroll every ancestor (the wizard column, the Settings page). */
function revealRow(list: HTMLElement, row: HTMLElement): void {
  const top = row.offsetTop;
  const bottom = top + row.offsetHeight;
  if (top < list.scrollTop) list.scrollTop = top;
  else if (bottom > list.scrollTop + list.clientHeight) list.scrollTop = bottom - list.clientHeight;
}

export function Select(props: SelectProps): React.JSX.Element {
  const { value, options, onChange, label, size = "md", placeholder, disabled, style, title, enterSubmits } = props;
  const id = useId();
  const rootRef = useRef<HTMLDivElement | null>(null);
  const triggerRef = useRef<HTMLButtonElement | null>(null);
  const listRef = useRef<HTMLDivElement | null>(null);
  const [open, setOpen] = useState(false);
  const [focused, setFocused] = useState(false);
  const [flipUp, setFlipUp] = useState(false);
  const [active, setActive] = useState(-1);
  const typeahead = useRef({ text: "", at: 0 });

  const selectedIdx = options.findIndex((o) => o.value === value);
  const selected = selectedIdx >= 0 ? options[selectedIdx] : undefined;

  const close = (refocus: boolean): void => {
    setOpen(false);
    if (refocus) triggerRef.current?.focus({ preventScroll: true });
  };

  const openList = (): void => {
    if (disabled || open) return;
    // The selected row is where the keyboard starts; without one, the first
    // pickable row, so ↓ never lands on nothing.
    setActive(selectedIdx >= 0 && !options[selectedIdx].disabled ? selectedIdx : firstEnabled(options));
    setOpen(true);
  };

  const pick = (i: number): void => {
    const opt = options[i];
    if (!opt || opt.disabled) return;
    close(true);
    if (opt.value !== value) onChange(opt.value);
  };

  // Placement + focus, before paint.
  useLayoutEffect(() => {
    if (!open) return;
    const trigger = triggerRef.current?.getBoundingClientRect();
    const list = listRef.current;
    if (trigger && list) {
      const band = visibleBand(list);
      const below = band.bottom - trigger.bottom;
      const above = trigger.top - band.top;
      setFlipUp(list.getBoundingClientRect().height + GAP > below && above > below);
      const row = list.querySelector<HTMLElement>('[aria-selected="true"]');
      if (row) revealRow(list, row);
    }
    list?.focus({ preventScroll: true });
  }, [open]);

  // The keyboard-active row stays in view inside the list.
  useEffect(() => {
    const list = listRef.current;
    const row = list?.querySelector<HTMLElement>(`[data-idx="${active}"]`);
    if (open && list && row) revealRow(list, row);
  }, [open, active]);

  // Options can change under an open list (a track catalogue that finishes
  // loading): keep `active` on a row that exists and can be picked.
  useEffect(() => {
    if (!open) return;
    setActive((a) => (a >= 0 && a < options.length && !options[a].disabled ? a : firstEnabled(options)));
    // Keyed on the list's LENGTH, not its identity: callers build it inline.
  }, [open, options.length]);

  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent): void => {
      if (!rootRef.current?.contains(e.target as Node)) setOpen(false);
    };
    window.addEventListener("mousedown", onDown);
    return () => window.removeEventListener("mousedown", onDown);
  }, [open]);

  const move = (i: number): void => {
    if (i >= 0) setActive(i);
  };

  const onTriggerKey = (e: KeyboardEvent<HTMLButtonElement>): void => {
    if (disabled) return;
    if (e.key === "Enter" && enterSubmits) {
      // Not ours to open: the frame reads this as "submit". preventDefault
      // stops the button's implicit click; the event still bubbles.
      e.preventDefault();
      return;
    }
    if (e.key === "ArrowDown" || e.key === "ArrowUp" || e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      openList();
    }
  };

  const onListKey = (e: KeyboardEvent<HTMLDivElement>): void => {
    const t = typeahead.current;
    const now = Date.now();
    const typing = !!t.text && now - t.at <= TYPEAHEAD_RESET_MS;
    switch (e.key) {
      case "ArrowDown": e.preventDefault(); move(nextEnabledIndex(options, active, 1)); return;
      case "ArrowUp": e.preventDefault(); move(nextEnabledIndex(options, active, -1)); return;
      case "Home": e.preventDefault(); move(firstEnabled(options)); return;
      case "End": e.preventDefault(); move(lastEnabled(options)); return;
      case "Enter":
        e.preventDefault(); e.stopPropagation(); pick(active); return;
      case " ":
        // Mid-word ("new c…") the space is a character; otherwise it picks.
        if (!typing) { e.preventDefault(); pick(active); return; }
        break;
      case "Escape":
        // Stop here so a host popover (the chat's ⋯ menu) does not close on
        // the same press — one Escape, one layer.
        e.preventDefault(); e.stopPropagation(); close(true); return;
      case "Tab":
        // Refocus the trigger first: the browser then tabs from THERE, so
        // focus lands on the next control instead of the document body.
        close(true); return;
    }
    if (e.key.length === 1 && !e.ctrlKey && !e.metaKey && !e.altKey) {
      e.preventDefault();
      t.text = (typing ? t.text : "") + e.key;
      t.at = now;
      move(typeAheadIndex(options, active, t.text));
    }
  };

  // Focus left the whole control (alt-tab, a screen-reader cursor): fold the
  // list without stealing focus back.
  const onListBlur = (e: FocusEvent<HTMLDivElement>): void => {
    if (!rootRef.current?.contains(e.relatedTarget as Node | null)) setOpen(false);
  };

  const sz = SIZE[size];
  const lit = open || focused;
  const labelId = `${id}-label`;
  const listId = `${id}-list`;

  return (
    <div ref={rootRef} style={{ position: "relative", minWidth: 0, ...style }}>
      {label && (
        <span id={labelId} style={{ ...fieldLabelStyle, display: "block" }}>{label}</span>
      )}
      <button
        ref={triggerRef}
        type="button"
        disabled={disabled}
        title={title}
        aria-label={props["aria-label"]}
        aria-labelledby={label ? labelId : undefined}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-controls={open ? listId : undefined}
        data-autofocus={props["data-autofocus"] ? "" : undefined}
        data-enter-submits={enterSubmits ? "" : undefined}
        onClick={() => (open ? close(true) : openList())}
        onKeyDown={onTriggerKey}
        onFocus={() => setFocused(true)}
        onBlur={() => setFocused(false)}
        style={{
          width: "100%", boxSizing: "border-box",
          display: "flex", alignItems: "center", gap: 8,
          padding: sz.padding, fontSize: sz.fontSize, fontFamily: "inherit", textAlign: "left",
          borderRadius: 10, border: `1px solid ${lit ? "var(--ql-yellow)" : "var(--ql-border)"}`,
          background: "rgba(255,255,255,.03)", color: "var(--ql-fg)",
          cursor: disabled ? "default" : "pointer", opacity: disabled ? 0.5 : 1, outline: "none",
          transition: "border-color .15s var(--ql-ease)",
        }}
      >
        {selected ? (
          <>
            {selected.icon && <IconTile>{selected.icon}</IconTile>}
            <span style={{ flex: 1, ...ELLIPSIS, ...selected.style }}>{selected.label}</span>
          </>
        ) : (
          <span style={{ flex: 1, ...ELLIPSIS, color: "var(--ql-fg-subtle)" }}>{placeholder ?? ""}</span>
        )}
        <ChevronDown
          size={14}
          aria-hidden
          style={{ flexShrink: 0, marginLeft: "auto", color: "var(--ql-fg-muted)", transform: open ? "rotate(180deg)" : "none", transition: "transform .15s var(--ql-ease)" }}
        />
      </button>

      {open && (
        <div
          ref={listRef}
          id={listId}
          role="listbox"
          tabIndex={-1}
          aria-labelledby={label ? labelId : undefined}
          aria-label={label ? undefined : props["aria-label"]}
          aria-activedescendant={active >= 0 ? `${id}-opt-${active}` : undefined}
          onKeyDown={onListKey}
          onBlur={onListBlur}
          // Rows are picked on click; the default mousedown would blur the
          // list first and the click would land on a closed menu.
          onMouseDown={(e) => e.preventDefault()}
          style={{
            position: "absolute", left: 0, zIndex: 40,
            ...(flipUp ? { bottom: `calc(100% + ${GAP}px)` } : { top: `calc(100% + ${GAP}px)` }),
            width: "100%", boxSizing: "border-box",
            maxHeight: 320, overflowY: "auto", padding: 5,
            background: "var(--ql-bg)", border: "1px solid var(--ql-border-strong)", borderRadius: 12,
            boxShadow: "0 18px 50px rgba(0,0,0,.55)", outline: "none",
            fontSize: sz.fontSize,
          }}
        >
          {options.map((opt, i) => {
            const isSel = i === selectedIdx;
            const isAct = i === active;
            return (
              <div
                key={`${opt.value}-${i}`}
                id={`${id}-opt-${i}`}
                data-idx={i}
                role="option"
                aria-selected={isSel}
                aria-disabled={opt.disabled || undefined}
                onClick={() => pick(i)}
                onMouseMove={() => !opt.disabled && !isAct && setActive(i)}
                style={{
                  display: "flex", alignItems: "center", gap: 8,
                  padding: "8px 9px", borderRadius: 8,
                  color: "var(--ql-fg)", fontFamily: "inherit",
                  background: isSel ? (isAct ? "rgba(255,213,0,.16)" : "rgba(255,213,0,.10)") : isAct ? "rgba(255,255,255,.06)" : "transparent",
                  opacity: opt.disabled ? 0.45 : 1,
                  cursor: opt.disabled ? "not-allowed" : "pointer",
                }}
              >
                {opt.icon && <IconTile>{opt.icon}</IconTile>}
                <span style={{ flex: 1, ...ELLIPSIS, ...opt.style }}>{opt.label}</span>
                {opt.hint && (
                  <span style={{ ...ELLIPSIS, maxWidth: "50%", fontSize: 11, color: "var(--ql-fg-subtle)" }}>{opt.hint}</span>
                )}
                {isSel && <Check size={14} aria-hidden style={{ flexShrink: 0, color: "var(--ql-yellow)" }} />}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
